import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import CaseCard from '../../features/cases/components/CaseCard';
import { useSavedCases } from '../../features/cases/hooks/use-cases';
import { timeAgo } from '../../utils/timeAgo';
import { useSearchParams } from 'react-router-dom';
import { Bookmark, ArrowLeft, Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isCoFounder } from '../../utils/cofounderBadge';

const SavedCasesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const { data, isLoading, isError } = useSavedCases(1, 20, q);

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="py-10 text-center text-red-500">
          Error loading cases. Please try again.
        </div>
      );
    }

    const cases = data?.data || [];

    if (cases.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          You haven't saved any cases yet.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {cases.map((backendCase) => {
          const isAuthorCurrentUser = Boolean(user?.id && (user.id === backendCase.authorId || user.id === backendCase.author?.id));
          const authorObj = backendCase.author || {};
          const rawName = authorObj.name || (isAuthorCurrentUser ? user?.name : 'Unknown');
          const authorName = authorObj.role === 'student' || isCoFounder(authorObj) || (isAuthorCurrentUser && isCoFounder(user)) ? rawName : (rawName.startsWith('Dr.') ? rawName : `Dr. ${rawName}`);
          const authorRole = authorObj.jobRole || (isAuthorCurrentUser ? user?.jobRole : null) || (authorObj.role === 'student' ? 'Student' : (authorObj.specialization || 'Veterinarian'));
          const authorAvatar = authorObj.avatarUrl || (isAuthorCurrentUser ? user?.avatarUrl : null);

          const mappedCase = {
            id: backendCase.id,
            author: authorName,
            specialty: authorRole,
            jobRole: authorRole,
            avatarUrl: authorAvatar,
            timeAgo: timeAgo(backendCase.createdAt),
            tags: [],
            title: backendCase.title,
            description: backendCase.content,
            hashtags: backendCase.tags || [],
            supportCount: backendCase.likes || 0,
            commentCount: backendCase.clinicalInputs?.length || backendCase._count?.clinicalInputs || 0,
            _restricted: backendCase._restricted,
            _accessStatus: backendCase._accessStatus,
            isRestricted: backendCase.isRestricted,
            postType: backendCase.postType,
            discussionType: backendCase.discussionType,
            media: backendCase.media || [],
          };
          return <CaseCard key={mappedCase.id} caseData={mappedCase} hideSave={true} />;
        })}
      </div>
    );
  };

  return (
    <AppShell>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto py-6 px-4">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
              <Bookmark className="text-accent-indigo" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Saved Cases</h1>
              <p className="text-gray-500 text-sm mt-1">Cases you've bookmarked for later review</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
          {q && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-indigo-900">
                <Search size={16} className="text-indigo-600" />
                <span>Showing saved cases matching "<span className="font-semibold">{q}</span>"</span>
              </div>
              <button
                onClick={clearSearch}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
              >
                <X size={14} /> Clear search
              </button>
            </div>
          )}
          {renderContent()}
        </div>
      </div>
    </AppShell>
  );
};

export default SavedCasesPage;
